import { lazy, Suspense, useMemo } from "react"
import { RouteObject } from "react-router"
import { QueryClient } from "@tanstack/react-query"
import { SkeletonPageLoading } from "@/components/shared/loading/page.tsx"
import { Skeleton } from "@/components/ui/skeleton.tsx"
import { importOrReload } from "@/lib/utils/dynamic-import.ts"
import { settingsLoader } from "@/feature/settings/api/loader.ts"

const LazySettingsIndexRoute = lazy(() =>
  importOrReload(() =>
    import(`./route.tsx`).then((mod) => ({
      default: mod.SettingsRoute,
    }))
  )
)

const LazySettingsAccountRoute = lazy(() =>
  importOrReload(() => import(`./account/route.tsx`))
)

const LazySettingsOrganizationRoute = lazy(() =>
  importOrReload(() => import(`./organization/route.tsx`))
)

const LazySettingsOrganizationCompaniesRoute = lazy(() =>
  importOrReload(() => import(`./organization/companies/route.tsx`))
)

const LazySettingsOrganizationEditRoute = lazy(() =>
  importOrReload(() => import(`./edit/organization/route.tsx`))
)

const LazySettingsCompanyEditRoute = lazy(() =>
  importOrReload(() => import(`./edit/company/route.tsx`))
)

const LazySettingsProfileEditRoute = lazy(() =>
  importOrReload(() => import(`./edit/profile/route.tsx`))
)

export const useSettingsRoute = (props: {
  queryClient: QueryClient
}): RouteObject[] =>
  useMemo(
    () => [
      {
        path: `settings`,
        element: <LazySettingsIndexRoute />,
        loader: settingsLoader(props.queryClient),
        hydrateFallbackElement: <SkeletonPageLoading />,
        children: [
          {
            index: true,
            element: (
              <Suspense fallback={<Skeleton className={`w-full h-full`} />}>
                <LazySettingsAccountRoute />
              </Suspense>
            ),
          },
          {
            path: `profile`,
            element: (
              <Suspense fallback={<Skeleton className={`w-full h-full`} />}>
                <LazySettingsAccountRoute />
              </Suspense>
            ),
          },
          {
            path: `organization`,
            element: (
              <Suspense fallback={<Skeleton className={`w-full h-full`} />}>
                <LazySettingsOrganizationRoute />
              </Suspense>
            ),
          },
          {
            path: `organization/companies`,
            element: (
              <Suspense fallback={<Skeleton className={`w-full h-full`} />}>
                <LazySettingsOrganizationCompaniesRoute />
              </Suspense>
            ),
          },
        ],
      },
      {
        path: `settings/organization/edit/:id`,
        element: (
          <Suspense fallback={<Skeleton className={`w-full h-full`} />}>
            <LazySettingsOrganizationEditRoute />
          </Suspense>
        ),
      },
      {
        path: `settings/company/edit/:id`,
        element: (
          <Suspense fallback={<Skeleton className={`w-full h-full`} />}>
            <LazySettingsCompanyEditRoute />
          </Suspense>
        ),
      },
      {
        path: `settings/profile/edit`,
        element: (
          <Suspense fallback={<Skeleton className={`w-full h-full`} />}>
            <LazySettingsProfileEditRoute />
          </Suspense>
        ),
      },
    ],
    [props.queryClient]
  )
